<template>
    <v-container fluid justify-start class="pt-0">
    
      <loading
        slot="activator"
        :active.sync="isDeleting"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />
  
      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="filteredZips"
        item-key="filename"
        :search="search"
        :loading="isLoading"
        :no-data-text="noDataText"
        :loading-text="getExpression('Alert', 'LoadingItems')"
        class="elevation-2"
        >
  
        <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />
  
        <template v-slot:top>
  
          <!-- Toolbar -->
          <v-toolbar dense dark flat :color="toolbarColor" class="mt-3">
  
            <app-back-button v-bind:color="toolbarColorDark" />
  
            <v-toolbar-title :class="titleClass">Abby Update Zips</v-toolbar-title>
  
            <v-spacer />
            
            <v-toolbar-items>
            
            <!-- Activate Button -->
            <v-btn @click="gotoRoute('UpdateOverrideAbbys')" :color="toolbarColorDark" dark class="elevation-0 mt-0 ml-2">
              <span>Manage Override Kiosks</span>
            </v-btn>

            <!-- Release Button -->
            <v-btn @click="$refs.release.click()" :color="toolbarColorDark" dark class="elevation-0 mt-0 ml-2">
              <v-icon class="mr-2">cloud_upload</v-icon>
              <span>Release</span>
            </v-btn>

            <input
              v-show="false"
              type="file"
              ref="release"
              accept=".zip"
              @change="fileChanged()"
            />

            </v-toolbar-items>
          </v-toolbar>
  
          <!-- Filters -->
          <v-row no-gutters class="mx-3 mt-2">
  
            <!-- Version -->
            <v-col cols="12" md="2">
              <v-select
                v-model="version"
                :items="[21, 22]"
                label='Version'
                placeholder="Select version"
                class="mr-3"
                >
              </v-select>
            </v-col>
  
            <!-- Search Text -->
            <v-col cols="12" md="10">
              <v-text-field
                v-model="search"
                append-icon="search"
                label="Search"
                clearable
              />
            </v-col>
          </v-row>
        </template>

        <template v-slot:item.size="{ item }">
            <span class="text-left">{{ getFormattedSize(item.size, 1) }}</span>
          </template>

        <template v-slot:item.createDate="{ item }">
          <span class="text-left"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/></span>
        </template>

        <template v-slot:item.action="{ item }">
            <v-icon v-if="item.canDelete" @click="remove(item)">
              delete
            </v-icon>
            <v-icon v-else color="grey lighten-1">delete</v-icon>
          </template>
      </v-data-table>
  
    </v-container>
  </template>
  
  <script>
  import appService from '@/app.service'
  import AppDate from '@/components/App/AppDate'
  import AppBackButton from '@/components/App/AppBackButton'
  import { AppColor, AppStyle } from '@/config.js'
  
  export default {
    components: {
      AppDate,
      AppBackButton
    },
    name: 'AbbyUpdateZipManager',
    data() {
      return {
        // colors
        appColor_Progress: AppColor.Progress,
        toolbarColor: AppColor.Medium,
        toolbarColorDark: AppColor.Dark,
  
        // styles
        appStyle_HyperLink: AppStyle.HyperLink,
  
        version: 22,
        updateZips: [],
  
        isLoading: true,
        isSaving: false,
        isDeleting: false,
        search: '',
        sortBy: 'createDate',
        descending: true,

        uploadZipFile: null,

        editedIndex: -1,

        headers: [
          {
            text: 'Filename',
            value: 'filename',
            align: 'left',
            width: '30%',
            sortable: false,
            class: 'pr-0',
          },
          {
            text: 'Size',
            value: 'size',
            align: 'left',
            width: '30%',
            sortable: false,
            class: 'pr-0'
          },
          {
            text: 'Created',
            value: 'createDate',
            align: 'left',
            width: '30%',
            sortable: false,
            class: 'pr-0'
          },
          {
            value: 'action',
            sortable: false,
            width: '10%',
            class: 'pl-0'
          }
        ]
      }
    },
    computed: {
      noDataText () {
        const self = this
  
        if (self.isLoading) return 'One moment...'
  
        return 'No zip files found'
      },
      filteredZips () {
        if (this.isLoading) return []

        const self = this
        var versionZips = self.updateZips.filter(uz => uz.phidgetVersion == self.version)
        return versionZips.length > 0
          ? versionZips[0].zipFiles
          : []
      },
      titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
      }
    },
    methods: {
      loadData () {
        if (!this.isValidSession) return
        const self = this

        self.isLoading = true
        appService.getAbbyUpdateZips().then(data => {
          if (data.errorSeverity > 0)
            self.handleError(data)
          else {
            data.updateZips.forEach(uz => {
              uz.zipFiles.splice(0, 1)
            })
            self.updateZips = data.updateZips
          }

          self.isLoading = false
        }).catch(e => { self.handleCriticalError(e) })
      },

      remove (item) {
        const self = this
        const index = self.filteredZips.findIndex(zf => zf.filename === item.filename)
        if (index < 0) return
        
        const updateLevel = item.filename.split('.').slice(0, -1).join('.')

        self.$confirm.show({
          title: 'Delete Update?',
          text: `<b>Filename: ${item.filename}</b>`,
          onConfirm: () => {
            self.isDeleting = true

            appService.deleteAbbyUpdateZip(self.version, updateLevel).then((data) => {
              self.isDeleting = false
              if (data.errorSeverity > 0) {
                self.handleError(data)
              } else {
                self.filteredZips.splice(index, 1)
              }
            }).catch(e => {
              self.handleCriticalError(e)
            })
          },
          onCancel: () => {
            self.resetFileInput()
          }
        }).catch(e => { self.handleCriticalError(e) })
      },

      fileChanged () {
        const file = this.$refs.release.files[0]
        const ext = file.name.split('.').pop()
        if (ext !== 'zip') {
          this.$error.show({
            title: 'Invalid Filename',
            text: `Filename not valid.<br/><br/>Expected a zip file.`,
            severity: 0
          })
        } else {
          this.uploadZipFile = file
          this.release()
        }
      },

      release () {
        const self = this

        self.$confirm.show({
          title: 'Release Update?',
          text: `Filename: <b>${self.uploadZipFile.name}</b></br>Version: <b>${self.version}</b>`,
          formColor: 'green',
          width: '230',
          onConfirm: () => {
            self.uploadRelease()
          },
          onCancel: () => {
            self.resetFileInput()
          }
        })
      },

      uploadRelease () {
        const self = this

        const newFilename = self.uploadZipFile.name
        const existingZips = self.filteredZips.filter(fz => fz.filename === newFilename)
        if (existingZips.length > 0 || newFilename === '0.zip') {
          self.handleError({
            errorSeverity: 0,
            errorMessage: `The file <b>${newFilename}</b> already exists`
          })
        } else {
          let formData = new FormData()
          formData.append('file', self.uploadZipFile)

          const url = `abbyupdates/${self.version}`
          self.$upload.execute({
            formData: formData,
            url: url,
            fileCount: 1,
            onComplete: (data) => {
              if (data.errorSeverity > 0) {
                self.handleError(data)
              } else {
                self.resetFileInput()
                if (data.zipFile !== null)
                  var versionFiles = self.updateZips.filter(uz => uz.phidgetVersion === self.version)
                  var files = versionFiles.length > 0
                    ? versionFiles[0].zipFiles 
                    : []

                  files.push(data.zipFile)
              }
            }
          })
        }
      },

      resetFileInput () {
        this.$refs.release.type = 'hidden'
        this.$refs.release.type = 'file'
      },

      gotoRoute(name) {
        this.$store.dispatch('pushRoute', { name: name })
        this.$router.push({ name: name })
      },

      handleError(result) {
        this.$error.show({
          title: result.errorSeverity === 0
            ? 'Validation Failed'
            : 'Internal Error',
          text: result.errorMessage,
          severity: result.errorSeverity
        })
      }
    },
    created() {
      this.loadData()
    }
  }
  </script>
  